<template lang="html">
    <div class="hydrologic-variability-map">
        <leaflet-map
            :id="id"
            class="candidates-map"
            :features="getMapFeatures()"
            :tiles="tilesConfig"
            @load="mapLoaded"
            @tiles:load="tilesLoaded"
            @error="mapError"
        />

        <div
            class="hydrologic-variability-map-legend is-centered has-text-grey
                has-background-grey-lighter columns is-multiline is-mobile"
        >
            <span class="legend-item column">
                <map-marker
                    :fill="queryMarkerColor"
                    class="marker marker-click"
                />
                <div class="marker-label">
                    Query Watershed
                </div>
            </span>
            <span
                v-for="(candidate, index) in candidateBasins"
                :key="index"
                class="legend-item column"
            >
                <span
                    class="dot candidate-2"
                    :style="{ 'background-color': colorScheme[index] }"
                />
                <div class="marker-label">
                    {{ candidate.candidate }}
                </div>
            </span>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import LeafletMap from '@foundryspatial/leaflet-map';
import MapMarker from '@foundryspatial/map-marker';
import {
    feature,
    point,
} from '@turf/helpers';

export default {
    name: 'HydrologicVariabilityMap',

    components: {
        LeafletMap,
        MapMarker,
    },

    props: {
        // map ID
        id: {
            type: String,
            default: 'hydrologic-variability-map',
        },

        // mapbox access token
        accessToken: {
            type: String,
            default: process.env.VUE_APP_MAPBOX_TOKEN,
        },

        // mapbox style URL
        mapStyle: {
            type: String,
            default: process.env.VUE_APP_MAPBOX_WATERSHED_DEFAULT_STYLE,
        },

        // array of objects containing candidates and their polygon
        candidateBasins: {
            type: Array,
            required: true,
        },

        // label text for query location
        queryLocationText: {
            type: String,
            default: 'Click location',
        },

        // object with "polygon" geojson
        queryBasin: {
            type: Object,
            required: true,
        },

        // click coordinates as a lngLat object
        queryCoords: {
            type: Object,
            required: true,
        },

        // array of colors for candidates
        colorScheme: {
            type: Array,
            required: true,
        },

        // query watershed fill color
        queryWatershedColor: {
            type: String,
            default: '#ccc',
        },

        // query watershed outline color
        queryWatershedOutlineColor: {
            type: String,
            default() {
                // fill color
                return this.queryWatershedColor;
            },
        },

        // "click location" map marker color
        queryMarkerColor: {
            type: String,
            default: '#ccc',
        },
    },

    computed: {
        // map tile configuration with mapbox style
        tilesConfig() {
            return {
                url: `https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${this.accessToken}`,
                options: {
                    id: this.mapStyleId,
                    attribution: 'Foundry Spatial | © Mapbox © OpenStreetMap',
                },
            };
        },

        // parse mapbox style ID out of the full style URL
        mapStyleId() {
            const styleIdMatches = this.mapStyle.match(/^mapbox:\/\/styles\/(.+)/);
            return styleIdMatches[1];
        },
    },

    methods: {
        /**
         * returns an array of geoJSON features for Leaflet
         * @return {Array} ordered array of map features
         */
        getMapFeatures() {
            // create map features with style properties
            const queryPolygon = feature(this.queryBasin.polygon, {
                leafletStyles: {
                    color: this.queryWatershedOutlineColor,
                    fillColor: this.queryWatershedColor,
                    fillOpacity: 0.5,
                },
            });

            // add style properties to each candidate basin polygon
            const candidateFeatures = this.candidateBasins.map((candidate, index) => {
                const color = this.colorScheme[index];
                return feature(candidate.geom, {
                    leafletStyles: {
                        color,
                        fillOpacity: 0,
                        weight: 3,
                    },
                });
            });

            // add click point marker
            const queryPoint = point([
                this.queryCoords.lng,
                this.queryCoords.lat,
            ], { markerColor: this.queryMarkerColor });

            return [
                queryPolygon,
                ...candidateFeatures,
                queryPoint,
            ];
        },

        /**
         * event handler for map "load" event
         * @param  {L.Event} ev  leaflet event data object
         * @param {L.map} map Leaflet map object
         */
        mapLoaded(ev, map) {
            L.control.scale().addTo(map);
        },

        /**
         * event handler for map tile layer "load" event
         * fired every time the grid layer finishes loading all visible tiles
         */
        tilesLoaded() {
            // emit event when map is loaded and idle
            this.$emit('load');
        },

        /**
         * re-emit map error events
         * @param  {Object}   errorEvent  mapbox-gl error event
         */
        mapError(errorEvent) {
            this.$emit('error', errorEvent);
        },
    },

};
</script>

<style lang="scss">
.hydrologic-variability-map {
    $grey-light: #b5b5b5 !default;

    display: flex;
    flex-direction: column;

    .candidates-map {
        flex: 1;
    }

    .hydrologic-variability-map-legend {
        height: fit-content;
        border: 1px solid $grey-light;
        justify-content: space-around;
        padding: 0.5rem 1rem;
        margin: 0;

        .legend-item {
            font-size: 16px;
            display: flex;
            margin: 0;
            align-items: center;

            // override default column padding
            &.column {
                padding: 0 0.75rem;
            }

            .marker-click {
                width: 1.2em;
                margin-right: 0.6em;
                margin-top: 0.2em;
            }

            span.dot {
                width: 1em;
                height: 1em;
                border-radius: 0.5em;
                display: inline-block;
                margin-right: 0.6em;
                vertical-align: middle;
            }
        }
    }
}
</style>
